/**
 * KeyboardShortcuts — dialog com atalhos de teclado.
 * Abre com `?`.
 */
import { Keyboard } from "lucide-react";
import { useEffect, useState } from "react";

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const SHORTCUTS = [
  { keys: ["⌘", "K"], description: "Abrir busca / paleta de comandos" },
  { keys: ["?"], description: "Abrir este painel de atalhos" },
  { keys: ["⌘", "↵"], description: "Comentar (em campos de comment)" },
  { keys: ["Esc"], description: "Fechar diálogos abertos" },
  { keys: ["G", "D"], description: "Ir para Dashboard" },
  { keys: ["G", "C"], description: "Ir para Chamados" },
  { keys: ["G", "N"], description: "Ir para Técnicos" },
  { keys: ["G", "I"], description: "Ir para Faturas" },
];

export function KeyboardShortcutsDialog() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Ignorar quando dentro de input/textarea
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || (e.target as HTMLElement)?.isContentEditable) return;

      if (e.key === "?") {
        e.preventDefault();
        setOpen(true);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Keyboard className="h-5 w-5 text-primary-600" />
            Atalhos de teclado
          </DialogTitle>
          <DialogDescription>
            Pressione <Kbd>?</Kbd> a qualquer momento pra ver este painel.
          </DialogDescription>
        </DialogHeader>

        <ul className="space-y-2 mt-2">
          {SHORTCUTS.map((s) => (
            <li key={s.description} className="flex items-center justify-between gap-3 py-1.5 border-b border-slate-200/40 dark:border-white/5 last:border-0">
              <span className="text-sm">{s.description}</span>
              <div className="flex items-center gap-1 shrink-0">
                {s.keys.map((k, i) => (
                  <span key={i} className="flex items-center gap-1">
                    <Kbd>{k}</Kbd>
                    {i < s.keys.length - 1 && <span className="text-xs text-muted-foreground">+</span>}
                  </span>
                ))}
              </div>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="inline-flex items-center justify-center min-w-7 h-7 px-2 rounded-md border border-slate-200/80 dark:border-white/10 bg-slate-50 dark:bg-white/[0.03] text-xs font-mono font-medium shadow-sm">
      {children}
    </kbd>
  );
}
